import type { OptionFeed } from "~/v2/api/rest/data";
import type { OptionContractWithSnapshot } from "../../hooks/useGetOptionsData";

/** Which side of the chain a cell belongs to. */
export type ChainSide = "call" | "put";

/** How many strikes to show either side of the money. */
export const STRIKE_COUNT_OPTIONS = [5, 10, 15, 20, 30, 50] as const;
export type StrikeCount = (typeof STRIKE_COUNT_OPTIONS)[number];
export const DEFAULT_STRIKE_COUNT: StrikeCount = 10;

/** Freshness of a value, which the chain labels per column rather than per row. */
export type ValueFreshness =
  /** Ticking, or polled within the expected window. */
  | "live"
  /** Structurally late — the default feed delays trades and volume 15 minutes. */
  | "delayed"
  /** Older than the delay explains, usually an illiquid contract. */
  | "stale"
  /** Daily batch, two trading days behind. */
  | "endOfDay"
  /** Frozen because the market is closed. */
  | "closed";

export interface ChainGreeks {
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
  rho?: number;
}

/** Every value the grid can render for one contract. */
export interface ChainQuote {
  bid?: number;
  ask?: number;
  /** Quote timestamp in ms, server-side where available. */
  quoteAt?: number;
  last?: number;
  lastAt?: number;
  /**
   * Midpoint of the quote. Derived, not published, and only defined when both
   * sides exist — a mid off a one-sided quote is a made-up price.
   */
  mark?: number;
  /**
   * Last trade against the previous session's close. Derived from `prevDailyBar`,
   * which is absent on roughly a third of contracts.
   */
  netChange?: number;
  /** `netChange` as a percentage of the previous close. */
  changePercent?: number;
  volume?: number;
  openInterest?: number;
  openInterestDate?: string;
  impliedVolatility?: number;
  greeks?: ChainGreeks;
}

/** Strike sort order. Strike is the only sortable column. */
/**
 * Which expirations the picker offers. Cadence is a grouping inside the menu
 * rather than a filter, so only the date range narrows the set.
 */
export type ExpirationFilter = "all" | "range";

export const EXPIRATION_FILTER_OPTIONS: readonly ExpirationFilter[] = [
  "all",
  "range",
];

/** Inclusive `YYYY-MM-DD` bounds, only meaningful when the filter is `range`. */
export interface ExpirationRange {
  from: string;
  to: string;
}

/**
 * Which edge of a pane the bid/ask pair hugs. With both sides on screen they
 * face each other across the strike column; with one side, both sit on the
 * right so the actionable cells are always in the same place.
 */
export type PinEdge = "left" | "right";

/** Which side of the chain to show. */
export type ChainSideFilter = "both" | "call" | "put";

export const CHAIN_SIDE_FILTER_OPTIONS: readonly ChainSideFilter[] = [
  "both",
  "call",
  "put",
];

/** One contract on one side of a strike. */
export interface ChainLeg {
  symbol: string;
  contract: OptionContractWithSnapshot;
  quote: ChainQuote;
  /** Calls below spot, puts above spot. */
  isInTheMoney: boolean;
  /** False when the contract exists but no snapshot came back for it. */
  hasMarketData: boolean;
}

/** One row of the two-sided grid. Strike pairing is reliable; a side is not. */
export interface ChainRow {
  strike: number;
  call?: ChainLeg;
  put?: ChainLeg;
  /** True for the strike nearest spot, or for both when spot sits between two. */
  isAtTheMoney: boolean;
  /**
   * The reading this row replaced, present only when a value actually moved. It
   * is what tells a cell which way it went, so the flash is driven by the data
   * rather than by each cell remembering its own history.
   */
  previous?: ChainRow;
}

/**
 * How many consecutive expirations the chain stacks, counting from the selected
 * one. More than one is what makes a calendar spread readable: both legs are on
 * screen, at the same strike, without changing the expiration in between.
 */
export const EXPIRATION_COUNT_OPTIONS = [1, 2, 3, 4, 6, 8, 12] as const;
export type ExpirationCount = (typeof EXPIRATION_COUNT_OPTIONS)[number];
/** One ladder by default: stacking is opt-in, for when a spread needs it. */
export const DEFAULT_EXPIRATION_COUNT: ExpirationCount = 1;

/** A stacked expiration and the strike ladder underneath it. */
export interface ChainSection {
  expiration: string;
  label: string;
  daysToExpiration: number;
  rows: ChainRow[];
}

/** `(W)` marks the weeklies; the rest of the cadence is not surfaced. */
export type ExpirationCadence = "weekly" | "monthly" | "quarterly";

export interface ExpirationOption {
  date: string;
  /** Calendar days to expiry, floored at zero. */
  daysToExpiration: number;
  cadence: ExpirationCadence;
}

/**
 * Everything the widget knows about how fresh and how trustworthy prices are.
 *
 * Every value is polled on a three-second interval. There is no live connection:
 * real-time streaming is PRIME-1135's ticket.
 */
export interface ChainFeedState {
  feed?: OptionFeed;
  /** Undefined until the clock resolves; the grid stays conservative until then. */
  isMarketOpen?: boolean;
}

/** Why the widget cannot show a chain, when it cannot. */
export type ChainEmptyReason =
  | "noSymbol"
  | "noContracts"
  | "unsupportedSymbol"
  | "noExpirations"
  | "noExpirationsInFilter";

export interface ChainPermissions {
  /**
   * Effective options level — the minimum of the account level and the user,
   * admin and correspondent caps. `0` or nullish means the user may look but
   * not trade.
   */
  optionsTradingLevel?: number | null;
  canTrade: boolean;
}
